import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { AppUrls } from '../../../app/AppUrls';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { IContentDefinition } from './IContentDefinition';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { DateTimeCell } from '../../components/listView/dataGridCells/DateTimeCells';
import { CommonColumn } from '../../components/listView/types/Columns';
import { OrderKeys } from '../sort/OrderKeys';
import { CollectionsPrivateIndicator } from '../../components/listView/dataGridCells/CollectionsPrivateIndicator';
import { LocalizedCountCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { ILink, LinkCell } from '../../components/listView/dataGridCells/LinkCell';
import { DataGrid } from '@tableau/widgets-datagrid';
import { Collection, SearchResultItem } from '../SearchResultTypes';
import { MetaDataResults } from '../httpClient/MetaDataResults';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import { SortBy } from '../SortSelection';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { FeatureFlag, FeatureFlagService } from '../../services/FeatureFlagService';
import { SessionManagementService } from '../../services/SessionManagementService';
import { CollectionFavoriteStateService } from '../../services/CollectionFavoriteStateService';
import { ListResult } from '../httpClient/ListResults';

export const collectionsContentDefinition: IContentDefinition<Collection, MetaDataResults> = {
  getSectionTitle: () => Messages.CollectionSectionTitle(),
  getUrl: (urls) => urls.searchCollectionsUrl(),
  getPath: (urls) => urls.searchCollectionsPath(),
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        crassData: crassDataItem,
        contentType: ContentType.collection,
        name: crassDataItem.title,
        id: crassDataItem.id.toString(),
        luid: crassDataItem.luid,
        owner: {
          displayName: crassDataItem.ownerName || '',
          domainName: crassDataItem.ownerDomain || '',
          id: crassDataItem.ownerId?.toString() || '',
          username: crassDataItem.ownerAlias || '',
          readOnly: false,
        },
        modifiedTime: crassDataItem.modifiedTime || '',
        description: crassDataItem.description || '',
        createdTime: crassDataItem.createdTime || '',
        itemCount: crassDataItem.itemCount,
        visibility: crassDataItem.visibility || '',
        itemNames: crassDataItem.itemNames,
      };
    });
  },
  mapMetadata: (searchResultItems, metadata) => {
    const favoriteMetaData: { [luid: string]: boolean } = {};
    if (metadata.total > 0) {
      metadata.items.favorites.forEach((luid) => {
        favoriteMetaData[luid] = true;
      });
    }
    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      return {
        ...searchResultItem,
        favorite:
          crassDataItem.luid && favoriteMetaData[crassDataItem.luid.toString()] ? favoriteMetaData[crassDataItem.luid.toString()] : false,
      };
    });
  },
  defaultDisplayMode: 'list',
  maxItemCountInMixedContentTypeResultPage: 4,
  contentSpaceUrl: (urls: AppUrls, contentId: string, contentLuid: string) => urls.collectionUrl(contentLuid),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter],
  listViewColumns: [
    (parameters: IColumnFactoryParameters) => {
      return {
        name: CommonColumn.Name,
        dataKey: OrderKeys.name,
        sortable: false,
        headerText: Messages.column_name(),
        direction: 'asc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LinkCell(
          (row: DataGrid.Row, testId?: string): ILink => {
            const collection = row.data as Collection;
            const link = parameters.generateLinkForName(row);
            link.afterTextComponent = collection.visibility === 'private' ? CollectionsPrivateIndicator : undefined;
            return link;
          },
          true,
          parameters.listViewParams.urls,
          parameters.listViewParams.searchTerm,
          parameters.listViewParams.total
        ),
        growToFit: true,
      };
    },
    CommonColumns.owner,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: CommonColumn.CollectionItemCount,
        dataKey: OrderKeys.permissionedItemCount,
        headerText: Messages.collectionItemCountColumn(),
        sortable: false,
        direction: 'desc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell('itemCount'),
        growToFit: true,
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'created',
        dataKey: 'createdTime',
        sortable: false,
        headerText: Messages.column_created(),
        direction: 'desc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DateTimeCell('createdTime'),
        growToFit: true,
      };
    },
    CommonColumns.description,
  ],
  getSortOptions: () => [SortBy.getRelevancySortSelection()],
  shouldBeAvailable: () =>
    !FeatureFlagService.instance.getValue(FeatureFlag.collectionsSiteSetting) || SessionManagementService.instance.collectionsEnabled(),
  favoritable: () => true,
  getCustomMetaDataRequests: (idList: string[]) => {
    return new Promise<ListResult<any>>((resolve, reject) => {
      if (FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224)) {
        setTimeout(() => {
          const promises: Promise<boolean>[] = [];
          idList.forEach((id) => {
            promises.push(CollectionFavoriteStateService.instance.getCollectionFavoriteState(id));
          });
          const favoriteIds: string[] = [];
          Promise.all(promises)
            .then((values) => {
              for (let i = 0; i < values.length; i++) {
                if (values[i]) {
                  favoriteIds.push(idList[i]);
                }
              }
              resolve(
                new ListResult({ favorites: favoriteIds, totalCount: favoriteIds.length } as MetaDataResults, undefined, favoriteIds.length)
              );
            })
            .catch(() => {
              reject(new ListResult({ favorites: [], totalCount: 0 } as MetaDataResults, undefined, 0));
            });
        }, 1000);
      } else {
        resolve(new ListResult({ favorites: [], totalCount: 0 } as MetaDataResults, undefined, 0));
      }
    });
  },
  getCustomID: (item: SearchResultItem) => {
    return (item as Collection).luid || '';
  },
};
